import React, { memo, useMemo, useState } from 'react';
import {
  OpportunityApproverRoles,
  PrismTransaction,
  useAgUsername,
  useDeclineCeoMutation,
  useDeclineOperationsMutation,
  useGetPrismTransactionsQuery,
  useGetReasonCodesQuery,
  useLazyGetPrismTransactionByIdQuery,
  useQualifyCeoMutation,
  useQualifyOperationsMutation
} from '@repo/services';
import { Button, Card, Grid, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { PersonByEmail } from '../PersonByEmail';
import {
  IconCircleCheck,
  IconCircleMinus,
  IconCircleX,
  IconThumbDown,
  IconThumbUp
} from '@tabler/icons-react';
import { IDynamicPerson } from '@microsoft/mgt-react';
import {
  DeviceType,
  errorBoundaryWrapper,
  stringHasValue,
  useAgLazyQuery,
  useAgMutation,
  useDeviceDetection
} from '@repo/utils';
import { isNotEmpty, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import OpportunityApproveModal from './OpportunityApproveModal';
import OpportunityDeclineModal from './OpportunityDeclineModal';

interface OpportunityApproverCardProps {
    transactionId: number | undefined;
    role: OpportunityApproverRoles;
    hasQualifierRole: boolean;
    /**
     * Called after a decline (Operations or CEO) mutation succeeds. Declining a
     * transaction moves it to the terminal "Declined" phase, which has a GIS
     * PotentialStatus equivalent (Discontinued) — the host app should use this
     * to trigger its own GIS sync, since this shared package can't depend on an
     * app-specific map context.
     */
    onDeclineSuccess?: () => void;
}

const OpportunityApproverCard = ({ transactionId, role, hasQualifierRole, onDeclineSuccess }: OpportunityApproverCardProps) => {
    const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);
    const [declineModalOpen, setDeclineModalOpen] = useState<boolean>(false);

    const username = useAgUsername();
    const device = useDeviceDetection();

  const { data: reasonCodes = [] } = useGetReasonCodesQuery(null);
    const [transactionToDisplay, transactionIsLoading] = useAgLazyQuery<PrismTransaction>(
        transactionId,
        useLazyGetPrismTransactionByIdQuery
    );
    const { refetch: refetchTransactions } = useGetPrismTransactionsQuery(null);
    const {
        trigger: triggerOperationsQualifyTransaction,
        isLoading: opsQualifyIsLoading
    } = useAgMutation<void>(useQualifyOperationsMutation,
        () => {
            refetchTransactions();
            notifications.show({
                title: "Qualify Successful",
                message: "Transaction Qualified Successfully"
            });
            closeApprovalModal();
        });

    const {
        trigger: triggerCeoQualifyTransaction,
        isLoading: ceoQualifyIsLoading
    } = useAgMutation<void>(useQualifyCeoMutation,
        () => {
            refetchTransactions();
            notifications.show({
                title: "Qualify Successful",
                message: "Transaction Qualified Successfully"
            });
            closeApprovalModal();
        });

    const {
        trigger: triggerOperationsDeclineTransaction,
        isLoading: opsDeclineIsLoading
    } = useAgMutation<void>(useDeclineOperationsMutation,
        () => {
            notifications.show({
                title: "Decline Successful",
                message: "Transaction Declined Successfully"
            });
            onDeclineSuccess?.();
            closeDeclineModal();
        });

    const {
        trigger: triggerCeoDeclineTransaction,
        isLoading: ceoDeclineIsLoading
    } = useAgMutation<void>(useDeclineCeoMutation,
        () => {
            notifications.show({
                title: "Decline Successful",
                message: "Transaction Declined Successfully"
            });
            onDeclineSuccess?.();
            closeDeclineModal();
        });

    const decisionData = useMemo(() => {
        const tx = transactionToDisplay;
        const opp = transactionToDisplay?.transactionQualification;
        if (opp) {
          return role === OpportunityApproverRoles.operationsVP
            ?
            {
              actor: opp?.operationsVpActor,
              email: opp?.operationsVpEmail,
              name: opp?.operationsVpName,
              decision: opp?.operationsVpDecision,
              reason: opp?.operationsVpDecisionReasonCode,
              comment: opp?.operationsVpDecisionComment
            }
            :
            {
              actor: opp?.ceoActor,
              email: opp?.ceoEmail,
              name: opp?.ceoName,
              decision: opp?.ceoDecision,
              reason: opp?.ceoDecisionReasonCode,
              comment: opp?.ceoDecisionComment
            }
        }
    },[role, transactionToDisplay, transactionToDisplay?.transactionQualification?.operationsVpDecision, transactionToDisplay?.transactionQualification?.ceoDecision])

    const canApprove = useMemo(() => {
        if (hasQualifierRole && transactionToDisplay?.status?.toLowerCase() === "pending") {
        // TODO: add check for user: if (decisionData.actor === username && opportunityToDisplay?.status?.toLowerCase() === "pending") {
            const opp = transactionToDisplay?.transactionQualification;
            if (opp) {
              const alreadyApproved = role === OpportunityApproverRoles.operationsVP
                ? stringHasValue(opp?.operationsVpDecision) && opp.operationsVpDecision?.toLowerCase() !== "tbd"
                : stringHasValue(opp.ceoDecision) && opp.ceoDecision?.toLowerCase() !== "tbd"
              if (alreadyApproved) return false;
              const isCeoAndOpsNotApproved = role === OpportunityApproverRoles.ceo
                && (!stringHasValue(opp?.operationsVpDecision) || opp.operationsVpDecision?.toLowerCase() === "tbd")
              return !isCeoAndOpsNotApproved;
            }
            return false
        }
        return false;
    },[decisionData, username, role, hasQualifierRole])

    const declineReasonCodes = useMemo(() => {
        return reasonCodes.filter(
            (reasonCode) => reasonCode.validForOpportunities && reasonCode.validForNegativeDecisions
        );
    }, [reasonCodes]);

    const form = useForm({
        initialValues: {
            reason: undefined,
            comments: ""
        },

        validate: {
            reason: declineModalOpen ? isNotEmpty('Reason is required') : undefined,
            comments: declineModalOpen ? isNotEmpty('Comments is required') : undefined
        }
    } as any);

    const closeApprovalModal = () => {
        setApproveModalOpen(false);
        form.reset();
    }

    const closeDeclineModal = () => {
        setDeclineModalOpen(false);
        form.reset();
    }

    const handleApprove = () => {
        if (role === OpportunityApproverRoles.operationsVP) {
            triggerOperationsQualifyTransaction({
                transactionId: transactionId,
                payload: {
                    decision: "YES",
                    comment: form.values.comments
                }
            });
        }
        if (role === OpportunityApproverRoles.ceo) {
            triggerCeoQualifyTransaction({
              transactionId: transactionId,
              payload: {
                  decision: "YES",
                  comment: form.values.comments
                }
            })
        }
    }

    const handleDecline = () => {
        form.validate();
        const validationResult = form.validate();
        if (!validationResult.hasErrors) {
            if (role === OpportunityApproverRoles.operationsVP) {
                triggerOperationsDeclineTransaction({
                    transactionId: transactionId,
                    payload: {
                        reasonCode: form.values.reason,
                        comment: form.values.comments
                    }
                });
            }
            if (role === OpportunityApproverRoles.ceo) {
                triggerCeoDeclineTransaction({
                    transactionId: transactionId,
                    payload: {
                      reasonCode: form.values.reason,
                      comment: form.values.comments
                    }
                });
            }
        }
    }

    const timeStampText = useMemo(() => {
        let returnText = '';
        const isYes = decisionData?.decision?.toLowerCase() === 'yes';
        const isNo = decisionData?.decision?.toLowerCase() === 'no';
        const decisionMaker = role === OpportunityApproverRoles.operationsVP
            ? transactionToDisplay?.transactionQualification?.operationsVpName
            : transactionToDisplay?.transactionQualification?.ceoName;
        const decisionDate = role === OpportunityApproverRoles.operationsVP
            ? transactionToDisplay?.transactionQualification?.operationsVpDecisionDate
            : transactionToDisplay?.transactionQualification?.ceoDecisionDate;
        const formattedDecisionDate = dayjs(decisionDate || '').format('MM/DD/YYYY');
        if (isYes) {
            returnText = `Approved by ${decisionMaker} on ${formattedDecisionDate}`;
        }
        if (isNo) {
            returnText = `Declined by ${decisionMaker} on ${formattedDecisionDate}`;
        }
        return returnText;
    },[role, decisionData?.decision, transactionToDisplay?.transactionQualification])

    const showMobileView = device === DeviceType.mobile;

    const ReasonAndComments = memo(() => {
      return (
        <>
          {decisionData?.decision?.toLowerCase() === 'no' && (
            <Grid.Col span={12}>
              <Stack>
                <Title order={6}>Reason</Title>
                <Text>{decisionData?.reason || ""}</Text>
                {/*<Text>{capitalizeFirstLetterOnly(reasonCodes?.find(c => c.aimReasonCode === decisionData.reason)?.description || "")}</Text>*/}
              </Stack>
            </Grid.Col>
          )}
          <Grid.Col span={12}>
            {!canApprove && (
              <Stack>
                <Title order={6}>Comments</Title>
                <Text>{decisionData?.comment || "(No comments provided)"}</Text>
              </Stack>
            )}
          </Grid.Col>
        </>
      )
    })

    const mobileButtonProps = {
      h: '80px',
      w: '100%'
    }

    return (
        <>
          <OpportunityApproveModal
            form={form}
            approveModalOpen={approveModalOpen}
            loading={role === OpportunityApproverRoles.operationsVP
              ? opsQualifyIsLoading
              : ceoQualifyIsLoading}
            setApproveModalOpen={setApproveModalOpen}
            closeApproveModal={closeApprovalModal}
            opportunityTitle={transactionToDisplay?.title || ""}
            handleApprove={handleApprove}
          />
          <OpportunityDeclineModal
            form={form}
            declineModalOpen={declineModalOpen}
            closeDeclineModal={closeDeclineModal}
            opportunityTitle={transactionToDisplay?.title || ""}
            loading={role === OpportunityApproverRoles.operationsVP
              ? opsQualifyIsLoading
              : ceoQualifyIsLoading}
            handleDecline={handleDecline}
          />
          <Card shadow="sm" radius="md" p={'md'} style={{ maxWidth: 800 }} withBorder>
            {transactionIsLoading && (
              <div style={{ textAlign: 'center' }}>
                <Loader size={27} />
              </div>
            )}
            {!transactionIsLoading && (
              <Grid>
                <Grid.Col span={showMobileView ? 12 : 2}>
                  <PersonByEmail
                    //@ts-ignore
                    view={"oneline"}
                    avatarSize='large'
                    fallbackDetails={{ mail: 'Loading Team Member...', displayName: decisionData?.name } as IDynamicPerson}
                    personCardInteraction={"click"}
                    email={decisionData?.email || ""}
                  />
                </Grid.Col>
                {!showMobileView && (
                  <Grid.Col span={6}></Grid.Col>
                )}
                {showMobileView && (
                  <ReasonAndComments />
                )}
                {!canApprove && (
                  <Grid.Col span={showMobileView ? 12 : 4} style={{ textAlign: "right" }}>
                    {decisionData?.decision?.toLowerCase() === 'yes' && (
                      <>
                        {showMobileView
                          ? (
                            <Button
                              color='green'
                              {...mobileButtonProps}
                              style={{ fontSize: '16px' }}
                            >
                              <Stack gap='xs' justify='center'>
                                <Group justify='center'>
                                  Approved
                                  <IconCircleCheck size={24} />
                                </Group>
                                <Text size='xs' w={'80%'} style={{ textWrap: 'wrap'}} fs="italic">{timeStampText}</Text>
                              </Stack>
                            </Button>
                          )
                            : (
                              <>
                                <Button color="green" mt={6} mb={6} rightSection={<IconCircleCheck size={14} />}>Approved</Button>
                                <Text size="xs" fs="italic">{timeStampText}</Text>
                              </>
                          )}

                      </>
                    )}
                    {decisionData?.decision?.toLowerCase() === 'no' && (
                      <>
                      {showMobileView
                        ? (
                          <Button
                            color='red'
                            {...mobileButtonProps}
                            style={{ fontSize: '16px' }}
                          >
                            <Stack gap='xs' justify='center'>
                              <Group justify='center'>
                                Declined
                                <IconCircleX size={24} />
                              </Group>
                              <Text size='xs' w={'80%'} style={{ textWrap: 'wrap'}} fs="italic">{timeStampText}</Text>
                            </Stack>
                          </Button>
                        )
                        : (
                          <>
                            <Button color="red" mt={6} mb={6} rightSection={<IconCircleX size={14} />}>Declined</Button>
                            <Text size="xs" fs="italic">{timeStampText}</Text>
                          </>
                        )}
                      </>
                    )}
                    {decisionData?.decision === '' || decisionData?.decision?.toLowerCase() === 'tbd' || !decisionData?.decision && (
                      <>
                        <Button color="gray" mt={6} {...(showMobileView ? mobileButtonProps : {})} rightSection={<IconCircleMinus size={14} />}>Pending Decision</Button>
                      </>
                    )}
                  </Grid.Col>
                )}
                {canApprove && (
                  <Grid.Col span={showMobileView ? 12 : 4} style={{ textAlign: "right" }}>
                    <Group justify="flex-end">
                      <Button
                        color="green"
                        mt={6}
                        {...(showMobileView ? mobileButtonProps : {})}
                        rightSection={<IconThumbUp size={14} />}
                        onClick={() => setApproveModalOpen(true)}
                        // onClick={handleApprove}
                        loading={role === OpportunityApproverRoles.operationsVP
                          ? opsQualifyIsLoading
                          : ceoQualifyIsLoading
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        color="red"
                        mt={6}
                        {...(showMobileView ? mobileButtonProps : {})}
                        rightSection={<IconThumbDown size={14} />}
                        onClick={() => setDeclineModalOpen(true)}
                        // onClick={handleDecline}
                        loading={role === OpportunityApproverRoles.operationsVP
                          ? opsDeclineIsLoading
                          : ceoDeclineIsLoading
                        }
                      >
                        Decline
                      </Button>
                    </Group>
                  </Grid.Col>
                )}
                {!showMobileView && (
                  <ReasonAndComments />
                )}
              </Grid>
            )}
          </Card>
        </>
    );
};

export default errorBoundaryWrapper(OpportunityApproverCard);
